import { useCallback } from "react"
import {
  Button,
  FlexGrid,
  FlexGridItem,
  LabelLarge,
  LabelSmall,
  MatsCircle,
  ParagraphSmall,
} from "@mezo-org/mezo-clay"
import { useLocalStorage } from "usehooks-ts"

import {
  selectClaimableMatsForBorrow,
  selectTotalMatsForBorrow,
  setOpenModal,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  DISABLE_TESTNET_MATS_REWARDS,
  LOCAL_STORAGE_BORROW_MATS_BANNER_VISIBLE,
} from "#/shared/constants"
import { useClaimMatsForBorrow, useMobileScreen } from "#/shared/hooks"
import { separateThousandsWithComma } from "#/shared/utils"

export default function BorrowMatsBanner() {
  const dispatch = useDappDispatch()
  const claimableMats = useDappSelector(selectClaimableMatsForBorrow)
  const totalMats = useDappSelector(selectTotalMatsForBorrow)

  const [isBannerVisible, setIsBannerVisible] = useLocalStorage<boolean>(
    LOCAL_STORAGE_BORROW_MATS_BANNER_VISIBLE,
    true,
  )

  const isMobileScreen = useMobileScreen()
  const { claim, isClaiming, hasClaimed } = useClaimMatsForBorrow()

  const hasNothingToClaim = hasClaimed || (!claimableMats && !!totalMats)

  const onClaim = useCallback(async () => {
    if (hasNothingToClaim) {
      setIsBannerVisible(false)
      return
    }

    setIsBannerVisible(true)

    const result = await claim()

    if (!result) {
      dispatch(setOpenModal("error-mats-claim"))
    }
  }, [hasNothingToClaim, setIsBannerVisible, claim, dispatch])

  if (isMobileScreen) return null

  if (!claimableMats && !totalMats) return null

  const claimableMatsFormatted = separateThousandsWithComma(claimableMats, 0, 0)

  if (!claimableMats || !isBannerVisible || DISABLE_TESTNET_MATS_REWARDS)
    return null

  return (
    <FlexGrid
      flexGridColumnCount={2}
      flexGridColumnGap="scale900"
      backgroundColor="purple300"
      padding="16px 24px"
      alignItems="center"
      marginBottom="scale1000"
      overrides={{ Block: { style: () => ({ borderRadius: "16px" }) } }}
    >
      <FlexGridItem>
        <LabelLarge color="#2E224C" marginBottom="4px">
          Congrats on getting your new loan 🎉
        </LabelLarge>
        <ParagraphSmall display="flex" alignItems="center">
          <LabelSmall as="span">{claimableMats}</LabelSmall>{" "}
          <MatsCircle color="transparent" />{" "}
          <LabelSmall as="span" marginRight="4px">
            mats
          </LabelSmall>{" "}
          have been issued to you
        </ParagraphSmall>
      </FlexGridItem>
      <FlexGridItem
        overrides={{
          Block: { style: () => ({ flexGrow: 0, width: "auto" }) },
        }}
      >
        <Button
          isLoading={isClaiming}
          onClick={onClaim}
          data-attr={hasNothingToClaim ? undefined : "banner-claim_mats"}
        >
          Claim {claimableMatsFormatted} mats
        </Button>
      </FlexGridItem>
    </FlexGrid>
  )
}
